import { differenceInCalendarDays, parseISO } from 'date-fns';
import type { Obligation, Reminder, Urgency } from '../types';

const DEFAULT_DUE_SOON_WINDOW = 30;

/**
 * How urgent is this obligation, right now.
 * - overdue: past its due date and still active
 * - due_soon: within 30 days (or within its own longest reminder window, if larger)
 * - ok: everything else
 */
export function getUrgency(
  obligation: Obligation,
  reminders: Reminder[],
  today: Date = new Date()
): Urgency {
  const daysUntilDue = differenceInCalendarDays(parseISO(obligation.due_date), today);
  if (daysUntilDue < 0) return 'overdue';

  const longestReminder = reminders.reduce(
    (max, r) => Math.max(max, r.offset_days),
    0
  );
  const window = Math.max(DEFAULT_DUE_SOON_WINDOW, longestReminder);

  if (daysUntilDue <= window) return 'due_soon';
  return 'ok';
}

export function daysUntil(dueDateISO: string, today: Date = new Date()): number {
  return differenceInCalendarDays(parseISO(dueDateISO), today);
}

export interface DashboardBuckets {
  overdue: Obligation[];
  today: Obligation[];
  comingUp: Obligation[]; // next 30 days, excluding today
  later: Obligation[]; // beyond 30 days
}

export function bucketObligations(
  obligations: Obligation[],
  today: Date = new Date()
): DashboardBuckets {
  const buckets: DashboardBuckets = { overdue: [], today: [], comingUp: [], later: [] };

  for (const o of obligations) {
    if (!o.active) continue;
    const d = daysUntil(o.due_date, today);
    if (d < 0) buckets.overdue.push(o);
    else if (d === 0) buckets.today.push(o);
    else if (d <= 30) buckets.comingUp.push(o);
    else buckets.later.push(o);
  }

  const byDate = (a: Obligation, b: Obligation) => (a.due_date < b.due_date ? -1 : 1);
  buckets.overdue.sort(byDate);
  buckets.comingUp.sort(byDate);
  buckets.later.sort(byDate);

  return buckets;
}

export interface NothingFallsThroughSummary {
  total: number;
  upToDate: number;
  dueSoon: number;
  overdue: number;
}

export function summarize(
  obligations: Obligation[],
  reminders: Record<string, Reminder[]>,
  today: Date = new Date()
): NothingFallsThroughSummary {
  const active = obligations.filter((o) => o.active);
  let upToDate = 0;
  let dueSoon = 0;
  let overdue = 0;

  for (const o of active) {
    const urgency = getUrgency(o, reminders[o.id] ?? [], today);
    if (urgency === 'overdue') overdue++;
    else if (urgency === 'due_soon') dueSoon++;
    else upToDate++;
  }

  return { total: active.length, upToDate, dueSoon, overdue };
}
